// SPDX-License-Identifier: Apache-2.0

/**
 * The available Layouts of the Map Grid.
 * The name of a Layout is <number of columns>x<number of rows>.
 */
export const LAYOUT_TYPES = ['1x1', '2x1', '1x2', '2x2'] as const;
export type LayoutType = (typeof LAYOUT_TYPES)[number];

/** Minimal size of a Column or of a Row of the Grid, in percent of the Grid. */
export const MIN_CELL_SIZE = 15;
/** Maximal size of a Column or of a Row of the Grid, in percent of the Grid. */
export const MAX_CELL_SIZE = 85;

/**
 * The Components that can be displayed in a Cell of the Grid.
 * To make a new Component available to the user, it is enough to add it to this list
 * (and to add its Element to the Grid in the HTML of the application).
 */
export const AVAILABLE_CELL_COMPONENTS = [
  { id: 'map-2d', tagName: 'girafe-map-2d', label: '2D Map' },
  { id: 'map-3d', tagName: 'girafe-map-3d', label: '3D Map' }
] as const;

export type CellComponent = (typeof AVAILABLE_CELL_COMPONENTS)[number]['id'];

/** The content of a Cell of the Grid: a Component, or null when the Cell is empty. */
export type CellContent = CellComponent | null;

export function isLayoutType(value: unknown): value is LayoutType {
  return LAYOUT_TYPES.includes(value as LayoutType);
}

export function isCellComponent(value: unknown): value is CellComponent {
  return AVAILABLE_CELL_COMPONENTS.some((component) => component.id === value);
}

/** The number of columns and of rows of a Layout. */
export function getGridSize(type: LayoutType) {
  const [columns, rows] = type.split('x').map(Number);
  return { columns, rows };
}

/** The sizes, in percent, of the equally distributed columns and rows of a Layout. */
export function getDefaultSizes(type: LayoutType) {
  const { columns, rows } = getGridSize(type);
  return {
    columns: new Array<number>(columns).fill(100 / columns),
    rows: new Array<number>(rows).fill(100 / rows)
  };
}

/**
 * Distributes the Components on the Cells of a Layout.
 * The Components that are already placed keep their Cell as long as it still exists,
 * and the Cells that are added by the new Layout are filled with the Components
 * that are not placed anywhere yet.
 */
export function distributeCells(type: LayoutType, currentCells: CellContent[]): CellContent[] {
  const { columns, rows } = getGridSize(type);
  const cellCount = columns * rows;

  const cells = currentCells.slice(0, cellCount);
  const unplacedComponents = AVAILABLE_CELL_COMPONENTS.map((component) => component.id).filter(
    (id) => !cells.includes(id)
  );
  while (cells.length < cellCount) {
    cells.push(unplacedComponents.shift() ?? null);
  }
  return cells;
}

/**
 * The Layout of the Map Grid: which Layout is displayed, which Component is displayed
 * in each of its Cells, and the sizes of its columns and rows (in percent),
 * which the user can change by dragging the splitters.
 *
 * The Cells are indexed row by row: in a 2x2 Layout, the Cell 0 is the top-left one,
 * the Cell 1 the top-right one, the Cell 2 the bottom-left one and the Cell 3 the bottom-right one.
 */
export default class LayoutState {
  public type: LayoutType = '1x1';
  public cells: CellContent[] = ['map-2d'];
  public sizes: { columns: number[]; rows: number[] } = { columns: [100], rows: [100] };
}
